import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { CreateUpdateProductFormValues } from "../productsTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { GetProductDetailsResponse, updateProduct, getProductDetails, MerchantProductsQuery } from "../productsApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { CreateUpdateProductForm } from "../components/CreateUpdateProductForm"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { toDinero, toNumberLossy } from "../../../../currency/currencyLib"
import { DateTime } from "luxon"

const selectProduct = (data: GetProductDetailsResponse) => {
    const product = data.product
    if (!product) return null

    return {
        productGroup: product.productGroup,
        productNumber: product.productNumber,
        name: product.name,
        price: toNumberLossy(toDinero(product.price)),
        currency: product.price.currency,
        shops: product?.shops ?? [],
        description: product.description,
        isActive: product.isActive,
        productPhoto: product.productPhoto,
        ...(product.availabilityDetails
            ? {
                  availabilityDetails: {
                      allowPreOrder: !product.availabilityDetails.allowPreOrder,
                      dateInterval: {
                          from: product.availabilityDetails?.dateInterval?.from
                              ? DateTime.fromISO(product.availabilityDetails?.dateInterval?.from)
                              : null,
                          to: product.availabilityDetails?.dateInterval?.to
                              ? DateTime.fromISO(product.availabilityDetails?.dateInterval?.to)
                              : null,
                      },
                  },
              }
            : {
                  availabilityDetails: {
                      allowPreOrder: false,
                  },
              }),
        ...(product.deadline
            ? {
                  deadline: {
                      purchases: {
                          daysBefore: Number(product.deadline.purchases.daysBefore),
                          timeOfDay: DateTime.fromISO(product.deadline.purchases.timeOfDay),
                      },
                      cancellation: {
                          daysBefore: Number(product.deadline.cancellation.daysBefore),
                          timeOfDay: DateTime.fromISO(product.deadline.cancellation.timeOfDay),
                      },
                  },
              }
            : {}),
        ...(product.offerDetails
            ? {
                  offerDetails: product.offerDetails,
              }
            : {}),
        ...(product.productDescription
            ? {
                  productDescription: product.productDescription,
              }
            : {}),
        // priceLists: product.priceLists
        //     ? product.priceLists.map((pl) => ({
        //           id: pl.id,
        //           name: pl.name,
        //           isActive: true,
        //       }))
        //     : [],
        priceList: product.priceLists?.[0]
            ? { id: product.priceLists[0].id, name: product.priceLists[0].name, isActive: true }
            : null,
        barcode: product?.barcode,
        paymentDetails: product.paymentDetails ?? {
            isPriceHidden: false,
        },
        deliveryDetails: product.deliveryDetails
            ? {
                  location: {
                      id: product.deliveryDetails.location.id,
                  },
              }
            : undefined,
        itemLimits: product.itemLimits || null,
        stock: product.stock
            ? { total: product.stock.inventory.total, itemsForSalePerDay: product.stock.sales?.daily?.itemsForSale }
            : null,
    }
}

const Product = forwardRef<FormikProps<CreateUpdateProductFormValues>>((props, ref) => {
    const { productId } = useParams()
    const navigate = useNavigate()

    const productResult = useApiQuery(getProductDetails, {
        queryName: MerchantProductsQuery.PRODUCT_DETAILS,
        dependencies: {
            productId: Number(productId) ?? 0,
        },
        enabled: Boolean(productId),
        select: selectProduct,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateProduct } = useApiMutation(updateProduct, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MerchantProductsQuery.PRODUCT_DETAILS, { productId: params.productId }),
            })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateProductForm
                ref={ref}
                initialValues={productResult.data}
                onSubmit={(data) => {
                    productId && callUpdateProduct({ product: data, productId: Number(productId) })
                }}
            />
        </>
    )
})

export function EditProductPage() {
    const productFormRef = useRef<FormikProps<CreateUpdateProductFormValues>>(null)

    const handleSaveClick = () => {
        if (productFormRef.current) {
            productFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="product-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRODUCTS}
                    title="Edit product"
                    rightElement={
                        <LocalizedStrict id="product-edit-submit-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE}>Products</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-update">
                            <Breadcrumb>Edit product</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Product ref={productFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
